import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./global-styles.css";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronRight,
  ExpandMore,
  Home,
  Settings,
  Event,
  Logout,
  Help,
  History,
} from "@mui/icons-material";

import { rolesConfig, hasRequiredRole } from "./rolesConfig";

const drawerWidth = 240;

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  width: "100%",
  left: 0,
  zIndex: theme.zIndex.drawer + 1,
  background: theme.palette.background.default,
  color: theme.palette.text.paper,
}));

const Drawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    boxShadow: theme.shadows[16],
  },
}));

// Define your tabs with allowedRoles properties for admin pages.
const TABS = {
  user: [
    { name: "Home", icon: <Home />, path: "/home" },
    { name: "Settings", icon: <Settings />, path: "/settings" },
    { name: "Help Center", icon: <Help />, path: "/help" },
  ],
  admin: [
    {
      name: "Events",
      icon: <Event />,
      path: "/events",
      allowedRoles: rolesConfig.eventsPage,
    },
    {
      name: "Logs",
      icon: <History />,
      path: "/logs",
      allowedRoles: rolesConfig.logsPage,
    },
    // { name: "Staff Stats", icon: <ShowChart />, path: "/staff-stats", allowedRoles: rolesConfig.staffStatsPage },
  ],
};

const Layout = ({ user, setUser, children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [open, setOpen] = useState(isDesktop);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUserArea, setShowUserArea] = useState(true);
  const [showAdminArea, setShowAdminArea] = useState(true);
  const [loadingTabs, setLoadingTabs] = useState(true);

  useEffect(() => {
    setOpen(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    if (user?.guildRoles !== undefined) {
      setLoadingTabs(false);
    }
  }, [user?.guildRoles]);

  const handleLogout = () => {
    localStorage.removeItem("discord_jwt");
    setUser(null);
    navigate("/login");
  };

  const handleTabClick = async (tab, isAdminTab) => {
    navigate(tab.path);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setOpen(!open)}
              sx={{
                mr: 2,
                display: { xs: "inline-flex", md: "none" },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" noWrap component="div" id="web-title">
                H.E.A.R.T.
              </Typography>
              <Typography variant="caption" noWrap component="div" id="subtext">
                Harvest's Event Administration & Resource Terminal
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="body1"
              className="welcome-text"
              sx={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: "1rem",
                color: "white",
                textAlign: "center",
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              Welcome, {user?.username}
            </Typography>
            <Avatar
              src={
                user?.avatar
                  ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${
                      user.avatar.startsWith("a_") ? "gif" : "png"
                    }`
                  : "https://cdn.discordapp.com/embed/avatars/0.png"
              }
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                cursor: "pointer",
                width: 40,
                height: 40,
                border: "2px solid #00d4ff",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? "permanent" : "temporary"}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ overflow: "auto" }}>
          <Toolbar sx={{ display: { xs: "none", md: "block" } }} />
          {loadingTabs ? (
            <Typography sx={{ p: 2 }}>Loading...</Typography>
          ) : (
            <>
              <List>
                <ListItemButton onClick={() => setShowUserArea(!showUserArea)}>
                  <ListItemText primary="User Area" />
                  {showUserArea ? <ExpandMore /> : <ChevronRight />}
                </ListItemButton>
                <Collapse in={showUserArea} timeout="auto" unmountOnExit>
                  {TABS.user.map((tab) => (
                    <ListItemButton
                      key={tab.name}
                      onClick={() => handleTabClick(tab, false)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon>{tab.icon}</ListItemIcon>
                      <ListItemText primary={tab.name} />
                    </ListItemButton>
                  ))}
                </Collapse>
              </List>

              {/* Only show admin area if the user has at least one admin role */}
              {user?.guildRoles &&
                TABS.admin.filter((tab) =>
                  hasRequiredRole(user, tab.allowedRoles)
                ).length > 0 && (
                  <>
                    <Divider />
                    <List>
                      <ListItemButton
                        onClick={() => setShowAdminArea(!showAdminArea)}
                      >
                        <ListItemText primary="Admin Area" />
                        {showAdminArea ? <ExpandMore /> : <ChevronRight />}
                      </ListItemButton>
                      <Collapse in={showAdminArea} timeout="auto" unmountOnExit>
                        {TABS.admin
                          .filter((tab) =>
                            hasRequiredRole(user, tab.allowedRoles)
                          )
                          .map((tab) => (
                            <ListItemButton
                              key={tab.name}
                              onClick={() => handleTabClick(tab, true)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon>{tab.icon}</ListItemIcon>
                              <ListItemText primary={tab.name} />
                            </ListItemButton>
                          ))}
                      </Collapse>
                    </List>
                  </>
                )}
            </>
          )}
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, height: "100vh" }}>
        <Toolbar />
        <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 2 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default Layout;
